import { useRef, useState } from 'react';
import { track, type Events } from './track';

type Outcome = 'success' | 'duplicate' | 'error';

// `source` tags every funnel event so the per-surface funnels stay separable
// in PostHog. `properties` are extra Mailjet contact properties a surface
// collects (the social textarea becomes social_data server-side); the server
// drops blanks and unknown names. A submit's own `details` — the contact's
// name, and properties a later step collects — ride on top of them for that
// request alone.
export function useWaitlistSignup(
  source: Events['landing_signup_submitted']['source'],
  properties?: { country: string; feedback?: string },
): {
  email: string;
  setEmail: (value: string) => void;
  social: string;
  setSocial: (value: string) => void;
  status: 'idle' | 'loading' | Outcome;
  // Resolves to the outcome `status` lands on, so a multi-step surface can
  // advance on it without watching the state. An empty email is a no-op
  // that reports 'error' and leaves the status alone.
  submit: (details?: {
    name?: string;
    properties?: { feedback?: string };
  }) => Promise<Outcome>;
  // Back to the form with the typed answers intact; a re-submit is a second
  // landing_signup_submitted, and Mailjet refreshes the same contact.
  backToForm: () => void;
} {
  const [emailValue, setEmailValue] = useState('');
  const [social, setSocial] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | Outcome>('idle');
  const emailStartedRef = useRef(false);

  const setEmail = (value: string) => {
    if (!emailStartedRef.current) {
      emailStartedRef.current = true;
      track('landing_email_started', { source });
    }
    setEmailValue(value);
  };

  const submit = async (details?: {
    name?: string;
    properties?: { feedback?: string };
  }): Promise<Outcome> => {
    if (emailValue === '') return 'error';
    const email_domain = emailValue.split('@')[1]?.toLowerCase() ?? '';
    const social_provided = Boolean(social.trim());
    setStatus('loading');
    track('landing_signup_submitted', {
      email_domain,
      source,
      social_provided,
    });
    const settle = (outcome: Outcome): Outcome => {
      setStatus(outcome);
      return outcome;
    };
    const allProperties = { ...properties, ...details?.properties };
    try {
      const res = await window.fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailValue,
          social,
          ...(details?.name !== undefined && { name: details.name }),
          ...(Object.keys(allProperties).length > 0 && {
            properties: allProperties,
          }),
        }),
      });
      if (res.ok) {
        const data: unknown = await res.json();
        if (
          typeof data === 'object' &&
          data !== null &&
          'alreadySubscribed' in data &&
          data.alreadySubscribed === true
        ) {
          track('landing_signup_duplicate', { email_domain, source });
          return settle('duplicate');
        }
        track('landing_signup_succeeded', {
          email_domain,
          source,
          social_provided,
        });
        return settle('success');
      }
      track('landing_signup_failed', {
        reason: 'non_ok',
        status: res.status,
        source,
      });
      return settle('error');
    } catch {
      track('landing_signup_failed', { reason: 'network_error', source });
      return settle('error');
    }
  };

  const backToForm = () => {
    setStatus('idle');
  };

  return {
    email: emailValue,
    setEmail,
    social,
    setSocial,
    status,
    submit,
    backToForm,
  };
}
